/**
 * VoiceMascot — maps the wireframer's `GenieMascot mascot="..."` keys to this
 * repo's brand mascot PNGs (`/genie-mascots/role/*.png`), wrapped in a soft
 * brand-tinted disc. Lightweight mapping (per Phase 0 locked decision) — no heavy
 * SVG sets ported.
 *
 * Brand law: tint is always `genie-primary` (#4E9CFF). No raw hex except the
 * signature gradient.
 */
import type { CSSProperties } from "react";
import type { VoiceMascotKey } from "@/types/voice-vision";

/** Wireframe mascot key -> repo mascot asset under /public/genie-mascots/role/. */
const MASCOT_SRC: Record<VoiceMascotKey, string> = {
  receptionist: "/genie-mascots/role/receptionist.png",
  support: "/genie-mascots/role/support.png",
  booking: "/genie-mascots/role/booking.png",
  sales: "/genie-mascots/role/sales.png",
  "onboarding-role": "/genie-mascots/role/onboarding.png",
  survey: "/genie-mascots/role/survey.png",
  feedback: "/genie-mascots/role/feedback.png",
  "after-hours": "/genie-mascots/role/after-hours.png",
  concierge: "/genie-mascots/role/concierge.png",
};

interface VoiceMascotProps {
  mascot: VoiceMascotKey;
  /** Pixel size of the mascot image (not the disc) */
  size?: number;
  /** Wrap in a soft brand-tinted circular disc */
  rounded?: boolean;
  /** Disc size when rounded (defaults to size * 1.15) */
  discSize?: number;
  className?: string;
  style?: CSSProperties;
}

export const VoiceMascot = ({
  mascot,
  size = 80,
  rounded = false,
  discSize,
  className,
  style,
}: VoiceMascotProps) => {
  const src = MASCOT_SRC[mascot] ?? MASCOT_SRC.receptionist;

  const img = (
    <img
      src={src}
      width={size}
      height={size}
      alt=""
      className={rounded ? undefined : className}
      style={{ objectFit: "contain", display: "block", flexShrink: 0, ...(rounded ? {} : style) }}
    />
  );

  if (!rounded) return img;

  const outer = discSize ?? Math.round(size * 1.15);
  return (
    <div
      className={`flex items-center justify-center rounded-full overflow-hidden bg-genie-primary/10 ${className ?? ""}`}
      style={{ width: outer, height: outer, ...style }}
    >
      {img}
    </div>
  );
};

export default VoiceMascot;
